import React, { useState, useMemo, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { AskRequest } from '@hone-automation/common';
import { prefixClass } from 'lib/utils';
import {
  Dropdown,
  IconButton,
  UserSearch,
  AvatarMini,
  deriveUserType,
  Tooltip,
} from 'presentation/components/KSQ2/Common';
import type { UserSearchUser, SpecialItemType } from 'presentation/components/KSQ2/Common';
import { useGetAllUsersByLocation } from 'hooks';
import { useLocationsStore } from 'hooks/useLocationsStore';
import { useAuthContext } from 'context/useAuthContext';
import { usePatchQueueRequestMutation } from 'hooks/queue/';
import { QueueView } from '@hone-automation/common';
import { useQueueContext } from 'presentation/components/Queue/QueueContext';
import { useQueryState } from 'hooks/useQueryState';
import { useConversationPanelContext } from '../../../../ConversationPanelContext';
import { IconAssignmentNew } from 'components/Icons';
import './AssignmentButton.scss';

export interface AssignmentButtonProps {
  request: AskRequest;
  /** Whether the current user can manage queue (KS user vs client) */
  canManageQueue?: boolean;
  /** Mobile mode - renders dropdown in portal to escape overflow:hidden containers */
  mobile?: boolean;
}

/**
 * Assignment button - Shows current assignee and allows changing via UserSearch dropdown
 */
export const AssignmentButton: React.FC<AssignmentButtonProps> = ({
  request,
  canManageQueue = true,
  mobile = false,
}) => {
  const prefix = prefixClass('conversation-header-assignment');
  const { locationId } = useParams<{ locationId: string }>();
  const { dispatch, state } = useQueueContext();
  const { requests, activeView } = state;
  const [, setRequestId] = useQueryState('requestId');
  const [isOpen, setIsOpen] = useState(false);
  const {
    mode,
    onRequestUpdate,
    refetchFlaggedTransactions,
    locationId: contextLocationId,
  } = useConversationPanelContext();
  const isStandaloneMode = mode === 'dataRowModal';
  const effectiveLocationId = contextLocationId || locationId;

  // Get current user for "Assign to me" option
  const { user: authUser, userId } = useAuthContext();
  const currentUser = useMemo(() => {
    if (!authUser || !userId) return undefined;
    return { userId, name: authUser.displayName || authUser.email?.split('@')[0] || 'Me' };
  }, [authUser, userId]);

  // Fetch users for the location
  const { data: locationUsers } = useGetAllUsersByLocation(effectiveLocationId);

  // Get current location name for "Assign to [Location]" label
  const currentLocation = useLocationsStore(state => state.currentLocation);
  const locationName = currentLocation?.name;

  /**
   * Highlight a filter with drop animation, then auto-clear
   * Maps assignment to QueueView and dispatches HIGHLIGHT_FILTER
   */
  const highlightFilter = useCallback(
    (targetFilter: QueueView) => {
      dispatch({ type: 'HIGHLIGHT_FILTER', payload: targetFilter });
      // Clear highlight after animation completes (500ms)
      setTimeout(() => {
        dispatch({ type: 'HIGHLIGHT_FILTER', payload: null });
      }, 500);
    },
    [dispatch]
  );

  /**
   * Determine target filter based on assignment result
   * Must match BrowserPanel's viewFilteredRequests logic:
   * - 'client': status === 'In Progress' (regardless of assignee)
   * - 'me': status === 'Open' && assignee === userId
   * - 'myTeam': status === 'Open' && assignee !== userId
   */
  const getTargetFilter = useCallback(
    (assignee: string | null, status: string): QueueView => {
      if (status === 'Closed') return 'Completed';
      if (status === 'In Progress') return 'Client'; // Status takes precedence
      if (assignee === userId) return 'Me';
      return 'My Team';
    },
    [userId]
  );

  const requestBelongsToView = useCallback(
    (req: AskRequest, view: QueueView): boolean => {
      if (req.priority === 'Blocker' && view === 'Blockers') return true;
      if (view === 'Completed') return req.status === 'Closed';
      if (view === 'Client') return req.status === 'In Progress';
      if (view === 'Me') return req.assignee === userId && req.status === 'Open';
      if (view === 'My Team') return req.assignee !== userId && req.status === 'Open';
      return false;
    },
    [userId]
  );

  const selectNextInView = useCallback(
    (excludeId: string) => {
      if (!activeView) return;
      const remainingInView = requests
        .filter(req => req.id !== excludeId && requestBelongsToView(req, activeView))
        .sort((a, b) => {
          const dateA = new Date(a.updatedAt || a.createdAt).getTime();
          const dateB = new Date(b.updatedAt || b.createdAt).getTime();
          return dateA - dateB;
        });
      const nextRequest = remainingInView[0] || null;
      dispatch({ type: 'SET_SELECTED_ITEM', payload: nextRequest });
      setRequestId(nextRequest?.id || undefined);
    },
    [activeView, requests, requestBelongsToView, dispatch, setRequestId]
  );

  // Find the currently assigned user
  const assignedUser = useMemo(() => {
    if (!request.assignee || !locationUsers) return null;
    return (locationUsers as any[]).find(u => u.userId === request.assignee) || null;
  }, [request.assignee, locationUsers]);

  // Check if request is internal (all comments are internal)
  const isInternalRequest = useMemo(() => {
    if (!request?.comments?.length) return false;
    return request.comments.every(comment => comment.visibility === 'Internal');
  }, [request?.comments]);

  // Patch mutation for updating assignee
  const patchMutation = usePatchQueueRequestMutation(data => {
    if (data) {
      // Standalone mode: use callbacks instead of QueueContext
      if (isStandaloneMode) {
        onRequestUpdate?.(data);
        refetchFlaggedTransactions?.();
        return;
      }

      // Update requests in context
      dispatch({
        type: 'SET_REQUESTS',
        payload: requests.map(req => (req.id === data.id ? data : req)),
      });

      // Determine target filter based on new assignment
      const targetFilter = getTargetFilter(data.assignee ?? null, data.status);

      // Highlight the target filter with animation
      highlightFilter(targetFilter);

      const movedOutOfView = activeView && targetFilter !== activeView;

      if (movedOutOfView && !mobile) {
        // Desktop: list + detail visible simultaneously — move cursor to next item
        selectNextInView(data.id);
      } else {
        // Mobile: only one surface at a time — update selectedItem so
        // MobileDetailsScreen can detect the view change and show the "moved" screen
        dispatch({ type: 'SET_SELECTED_ITEM', payload: data });
      }
    }
  });

  const handleUserSelect = (user: UserSearchUser) => {
    // Backend handles status changes based on assignee type
    patchMutation.mutate({ id: request.id, assignee: user.userId, locationId: request.locationId });
    setIsOpen(false);
  };

  const handleSpecialSelect = async (type: SpecialItemType, selectedUserId?: string) => {
    if (type === 'unassigned') {
      patchMutation.mutate({ id: request.id, assignee: null, locationId: request.locationId });
    } else if (type === 'assign-to-me' && selectedUserId) {
      patchMutation.mutate({ id: request.id, assignee: selectedUserId, locationId: request.locationId });
    } else if (type === 'assign-to-client') {
      // Assign to Client - clear assignee and change status to In Progress
      if (request.assignee) {
        await patchMutation.mutateAsync({ id: request.id, assignee: null, locationId: request.locationId });
      }
      patchMutation.mutate({ id: request.id, status: 'In Progress', locationId: request.locationId });
    } else if (type === 'assign-to-team') {
      // Assign to Team - clear assignee and change status to Open
      if (request.assignee) {
        await patchMutation.mutateAsync({ id: request.id, assignee: null, locationId: request.locationId });
      }
      patchMutation.mutate({ id: request.id, status: 'Open', locationId: request.locationId });
    }
    // KAI option hidden - uncomment when ready
    // else if (type === 'kai') {
    //   patchMutation.mutate({ id: request.id, assignee: null, locationId: request.locationId });
    // }
    setIsOpen(false);
  };

  const assigneeName = assignedUser?.name || assignedUser?.email?.split('@')[0];

  // Derive avatar type for assigned user
  const assignedUserType = assignedUser ? deriveUserType(assignedUser as UserSearchUser) : 'unassigned';

  const trigger = assignedUser ? (
    <Tooltip content={`Assigned to ${assigneeName}`} position="bottom" alignment="center">
      <AvatarMini id={assignedUser.userId} type={assignedUserType} name={assigneeName} variant="assigned" />
    </Tooltip>
  ) : (
    <IconButton icon={<IconAssignmentNew />} onClick={() => setIsOpen(true)} size="medium" tooltip="Assign" />
  );

  return (
    <Dropdown
      trigger={trigger}
      position="bottom"
      alignment="start"
      isOpen={isOpen}
      onOpenChange={setIsOpen}
      closeOnClickOutside={true}
      closeOnItemClick={false}
      className={prefix('__dropdown')}
      usePortal={mobile}
    >
      <UserSearch
        users={locationUsers}
        placeholder="Search team members..."
        onUserSelect={handleUserSelect}
        onSpecialSelect={handleSpecialSelect}
        onClose={() => setIsOpen(false)}
        isOpen={isOpen}
        showSpecialItems
        currentUser={currentUser}
        canManageQueue={canManageQueue}
        requestStatus={request.status}
        locationName={locationName}
        isInternalRequest={isInternalRequest}
      />
    </Dropdown>
  );
};

export default AssignmentButton;
